import { __ } from '@wordpress/i18n';
import { registerBlockType } from '@wordpress/blocks';
import { useBlockProps, InspectorControls } from '@wordpress/block-editor';
import { 
    PanelBody, 
    ToggleControl,
    TabPanel,
    TextControl,
    ButtonGroup,
    Button,
    RangeControl,
    Spinner
} from '@wordpress/components';
import ServerSideRender from '@wordpress/server-side-render';

// Import custom components
import Typography from '../../components/Typography';
import VisibilityControl from '../../components/VisibilityControl';
import FlatColorPicker from '../../components/FlatColorPicker';
import Spacing from '../../components/Spacing';
import Border from '../../components/Border';
import Loading from '../../utils/Loading';


registerBlockType('kanews/table-of-contents', {
    title: __('İçindekiler', 'kanews'),
    icon: 'list-view',
    category: 'kanews',
    attributes: {
        title: {
            type: 'string',
            default: __('İçindekiler', 'kanews')
        },
        showTitle: {
            type: 'boolean',
            default: true
        },
        showNumbers: {
            type: 'boolean',
            default: true
        },
        isAccordion: {
            type: 'boolean',
            default: false
        },
        headingLevels: {
            type: 'array',
            default: ['h1', 'h2', 'h3']
        },
        maxDepth: {
            type: 'number',
            default: 3
        },
        // Typography attributes
        titleTypography: {
            type: 'object',
            default: {}
        },
        linkTypography: {
            type: 'object',
            default: {}
        },
        // Style attributes
        titleColor: {
            type: 'string',
            default: ''
        },
        linkColor: {
            type: 'string',
            default: ''
        },
        linkHoverColor: {
            type: 'string',
            default: ''
        },
        backgroundColor: {
            type: 'string',
            default: ''
        },
        // Visibility
        visibility: {
            type: 'object',
            default: {
                desktop: false,
                tablet: false,
                mobile: false
            }
        },
        margin: {
            type: 'object',
            default: {
                desktop: {
                    top: '',
                    right: '',
                    bottom: '',
                    left: ''
                },
                tablet: {
                    top: '',
                    right: '',
                    bottom: '',
                    left: ''
                },
                mobile: {
                    top: '',
                    right: '',
                    bottom: '',
                    left: ''
                }
            }
        },
        padding: {
            type: 'object',
            default: {
                desktop: {
                    top: '',
                    right: '',
                    bottom: '',
                    left: ''
                },
                tablet: {
                    top: '',
                    right: '',
                    bottom: '',
                    left: ''
                },
                mobile: {
                    top: '',
                    right: '',
                    bottom: '',
                    left: ''
                }
            }
        },
        border: {
            type: 'object',
            default: {
                desktop: {
                    style: 'none',
                    width: {
                        top: '',
                        right: '',
                        bottom: '',
                        left: ''
                    },
                    radius: {
                        topLeft: '',
                        topRight: '',
                        bottomRight: '',
                        bottomLeft: ''
                    },
                    color: ''
                },
                tablet: {
                    style: 'none',
                    width: {
                        top: '',
                        right: '',
                        bottom: '',
                        left: ''
                    },
                    radius: {
                        topLeft: '',
                        topRight: '',
                        bottomRight: '',
                        bottomLeft: ''
                    },
                    color: ''
                },
                mobile: {
                    style: 'none',
                    width: {
                        top: '',
                        right: '',
                        bottom: '',
                        left: ''
                    },
                    radius: {
                        topLeft: '',
                        topRight: '',
                        bottomRight: '',
                        bottomLeft: ''
                    },
                    color: ''
                }
            }
        }
    },
    edit: function(props) {
        const { attributes, setAttributes } = props;
        const blockProps = useBlockProps();

        // Tab panel tabs
        const tabs = [
            {
                name: 'content',
                title: __('İçerik', 'kanews'),
                className: 'content-tab'
            },
            {
                name: 'design',
                title: __('Tasarım', 'kanews'),
                className: 'design-tab'
            },
            {
                name: 'visibility',
                title: __('Görünürlük', 'kanews'),
                className: 'visibility-tab'
            }
        ];

        // Toggle heading level
        const toggleHeadingLevel = (level) => {
            const currentLevels = [...attributes.headingLevels];
            const levelIndex = currentLevels.indexOf(level);
            
            if (levelIndex === -1) {
                // Add level if not present
                currentLevels.push(level);
            } else {
                // Remove level if present
                currentLevels.splice(levelIndex, 1);
            }
            
            setAttributes({ headingLevels: currentLevels });
        };

        return (
            <div {...blockProps}>
                <InspectorControls>
                    <TabPanel
                        className="kanews-tabs"
                        tabs={tabs}
                    >
                        {(tab) => {
                            return (
                                <div>
                                    {tab.name === 'content' && (
                                        <PanelBody title={__('İçerik Ayarları', 'kanews')} initialOpen={true}>
                                            <TextControl
                                                label={__('Başlık', 'kanews')}
                                                value={attributes.title}
                                                onChange={(value) => setAttributes({ title: value })}
                                            />
                                            <ToggleControl
                                                label={__('Başlık Göster', 'kanews')}
                                                checked={attributes.showTitle}
                                                onChange={(value) => setAttributes({ showTitle: value })}
                                            />
                                            <ToggleControl
                                                label={__('Numaraları Göster', 'kanews')}
                                                checked={attributes.showNumbers}
                                                onChange={(value) => setAttributes({ showNumbers: value })}
                                            />
                                            <ToggleControl
                                                label={__('Açılır Menü', 'kanews')}
                                                checked={attributes.isAccordion}
                                                onChange={(value) => setAttributes({ isAccordion: value })}
                                            />
                                            <RangeControl
                                                label={__('Maksimum Derinlik', 'kanews')}
                                                value={attributes.maxDepth}
                                                onChange={(value) => setAttributes({ maxDepth: value })}
                                                min={1}
                                                max={6}
                                            />
                                            <div className="components-base-control">
                                                <div className="components-base-control__field">
                                                    <label className="components-base-control__label">
                                                        {__('Heading Levels', 'kanews')}
                                                    </label>
                                                    <div className="components-button-group">
                                                        {['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].map((level) => (
                                                            <Button
                                                                key={level}
                                                                isPrimary={attributes.headingLevels.includes(level)}
                                                                onClick={() => toggleHeadingLevel(level)}
                                                                className="components-button-group__button"
                                                            >
                                                                {level.toUpperCase()}
                                                            </Button>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                            
                                        </PanelBody>
                                    )}
                                    
                                    {tab.name === 'design' && (
                                        <div>
                                            <PanelBody
                                                title={__('Renk Ayarları', 'kanews')}
                                                initialOpen={false}
                                            >
                                                <FlatColorPicker
                                                    label={__('Arka Plan Rengi', 'kanews')}
                                                    value={attributes.backgroundColor}
                                                    onChange={(value) => setAttributes({ backgroundColor: value })}
                                                />
                                                <FlatColorPicker
                                                    label={__('Başlık Rengi', 'kanews')}
                                                    value={attributes.titleColor}
                                                    onChange={(value) => setAttributes({ titleColor: value })}
                                                />
                                                <FlatColorPicker
                                                    label={__('Link Rengi', 'kanews')}
                                                    value={attributes.linkColor}
                                                    onChange={(value) => setAttributes({ linkColor: value })}
                                                />
                                                <FlatColorPicker
                                                    label={__('Link Hover Rengi', 'kanews')}
                                                    value={attributes.linkHoverColor}
                                                    onChange={(value) => setAttributes({ linkHoverColor: value })}
                                                />
                                            </PanelBody>

                                            <PanelBody
                                                title={__('Yazı Tipi Ayarları', 'kanews')}
                                                initialOpen={false}
                                            >
                                                <Typography
                                                    label={__('Başlık Yazı Tipi', 'kanews')}
                                                    value={attributes.titleTypography}
                                                    onChange={(value) => setAttributes({ titleTypography: value })}
                                                />
                                                <Typography
                                                    label={__('Link Yazı Tipi', 'kanews')}
                                                    value={attributes.linkTypography}
                                                    onChange={(value) => setAttributes({ linkTypography: value })}
                                                />
                                            </PanelBody>

                                            <PanelBody
                                                title={__('Boşluk Ayarları', 'kanews')}
                                                initialOpen={false}
                                            >
                                                <Spacing
                                                    label={__('Margin', 'kanews')}
                                                    value={attributes.margin}
                                                    onChange={(value) => setAttributes({ margin: value })}
                                                />
                                                <Spacing
                                                    label={__('Padding', 'kanews')}
                                                    value={attributes.padding}
                                                    onChange={(value) => setAttributes({ padding: value })}
                                                />
                                            </PanelBody>

                                            <PanelBody
                                                title={__('Kenarlık Ayarları', 'kanews')}
                                                initialOpen={false}
                                            >
                                                <Border
                                                    label={__('Kenarlık', 'kanews')}
                                                    value={attributes.border}
                                                    onChange={(value) => setAttributes({ border: value })}
                                                />
                                            </PanelBody>
                                        </div>
                                    )}
                                    
                                    {tab.name === 'visibility' && (
                                        <PanelBody title={__('Görünürlük Ayarları', 'kanews')} initialOpen={true}>
                                            <VisibilityControl
                                                value={attributes.visibility}
                                                onChange={(value) => setAttributes({ visibility: value })}
                                            />
                                            
                                        </PanelBody>
                                    )}
                                </div>
                            );
                        }}
                    </TabPanel>
                </InspectorControls>
                <ServerSideRender
                    block="kanews/table-of-contents"
                    attributes={attributes}
                    LoadingResponsePlaceholder={() => (
                        <Loading />
                    )}
                />
            </div>
        );
    },
    save: function() {
        return null; // Dynamic block, rendered on PHP side
    }
});